import React, { useEffect, useCallback, useState, useMemo } from 'react'
import StandardModalContentNew from 'GeneralUI/StandardModalContent/StandardModalContentNew.jsx'
import withWindowScreenWorkflowConfigs from './withWindowScreenWorkflowConfigs.js'
import InputGroupText from 'GeneralUI/inputGroupText/InputGroupText.jsx'
import { push, encodeItemURIComponents } from 'Helper/routeHelpers'
import ConfigureStepModal from '../Designer/ConfigureStepModal.jsx'
import { pathValidator } from '../../Helper/sysAdminValidators.js'
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'
import { openModal, closeModal } from 'actions/modalActions'
import DeleteWorkflowModal from './DeleteWorkflowModal.jsx'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { xtractAPI } from 'actions/apiActions'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import _ from 'lodash'
import {
  getNextAvailablePath,
  getErrorModalAction
} from './DesignerHelpers.jsx'

const EditScreenModal = (props) => {
  const {
    isEdit,
    closeSelf,
    startSpinner,
    endSpinnerWithSuccess,
    endSpinnerWithError,
    contextWorkflowConfigs,
    contextWorkflowConfigsId
  } = props

  const dispatch = useDispatch()
  const currentScreenType = getCurrentScreenType()
  const { pathname } = useLocation()
  const currentWorkflowPath = useSelector(state => state.router?.match.params.workflow)
  const currentStep = useSelector(state => state.router?.match?.params?.step) || 0

  const [titleState, setTitleState] = useState("")
  const [urlState, setUrlState] = useState("")
  const [workflowMenuState, setWorkflowMenuState] = useState(true)

  const nextAvailablePath = useMemo(() => {
    return getNextAvailablePath(contextWorkflowConfigs, 'workflow')
  }, [getNextAvailablePath, contextWorkflowConfigs])

  const currentWorkflowConfig = useMemo(() => {
    if(isEdit) {
      return contextWorkflowConfigs.find(workflow => workflow.path === currentWorkflowPath && (workflow.screenType === currentScreenType || (!workflow.screenType && currentScreenType === 'standard')));
    } else {
      return {name: "", path: nextAvailablePath, selectable: true}
    }
  }, [currentWorkflowPath, contextWorkflowConfigs, nextAvailablePath, currentScreenType])

  useEffect(() => {
    if(currentWorkflowConfig) {
      setTitleState(currentWorkflowConfig.name)
      setUrlState(currentWorkflowConfig.path)
      setWorkflowMenuState(currentWorkflowConfig.selectable)
    }
  }, [currentWorkflowConfig])

  const screenType = useMemo(getCurrentScreenType,[])

  useEffect(() => {
    // make api call to ensure screen data loads
    const searchObj = { or: [{equal: { name: 'screens' }}, {equal: { name: 'workflows' }}] }
    dispatch(xtractAPI('config', 'search', searchObj))
  }, [])

  const validateWorkflows = useCallback((workflows) => {
    let ErrorContent = "";
    if (workflows.some(workflow => {
      return !workflow.name || !workflow.path
    }))
      ErrorContent = <div>
        {"Each configured workflow must have a title and a URL name."}
      </div>

    const validationErrors = pathValidator(workflows, 'workflow')
    if (validationErrors)
      ErrorContent = <div>
        {"Each configured workflow must have a unique path. The following errors were detected:"}
        <ul>
          {validationErrors}
        </ul>
      </div>

    if (ErrorContent) {
      dispatch(getErrorModalAction('Validation error', ErrorContent))
      return false
    } else
      return true
  }, [getErrorModalAction])

  const isFormValid = useCallback(() => {
    if (!titleState) {
      dispatch(getErrorModalAction('Missing Title', 'Every workflow must have a title defined'))
      return false
    }

    return true
  }, [titleState, getErrorModalAction])

  const getNewWorkflow = useCallback(() => {
    return {
      name: titleState,
      path: urlState,
      selectable: workflowMenuState,
      screenType: screenType,
      sequence: []
    }
  }, [titleState, urlState, workflowMenuState, screenType])

  const openConfigureStepModal = useCallback((newWorkflow, isEdit) => {
    dispatch(openModal(
      'configure_step_modal',
      {
        title: 'Add Step',
        content: (
          <ConfigureStepModal
            selectedStepIndex = {currentStep - 1}
            workflowConfig={newWorkflow}
            isEdit={isEdit}
            closeSelf={() => dispatch(closeModal('configure_step_modal'))}
          />
        ),
        closeable: true
      })
    )
  }, [currentStep, openModal])

  const saveWorkflowsToApi = useCallback((workflows, callback = () => null) => {
    const workflowData = JSON.stringify(workflows.map(encodeItemURIComponents))

    const callbacks = {
      200: response => {
        endSpinnerWithSuccess()
        callback()
      },
      400: (response, action) => endSpinnerWithError({ action, response, errors: response.data.errors }),
      error: (response, action) => endSpinnerWithError({ response, action })
    }

    startSpinner()
    if(contextWorkflowConfigsId) {
      dispatch(xtractAPI(`config/${contextWorkflowConfigsId}`, 'update', { value: workflowData }, callbacks))
    } else {
      dispatch(xtractAPI(`config`, 'create', { value: workflowData }, callbacks))
    }
  }, [contextWorkflowConfigsId, openConfigureStepModal, endSpinnerWithError, endSpinnerWithSuccess, startSpinner, encodeItemURIComponents])

  const updateWorkflow = useCallback(() => {
    if (!isFormValid()) {
      return true
    }

    let allWorkflows = _.cloneDeep(contextWorkflowConfigs)
    let effectedWorkflow
    if(isEdit) {
      allWorkflows = allWorkflows.map(workflow => {
        if (workflow.path === urlState && (workflow.screenType === currentScreenType || (!workflow.screenType && currentScreenType === 'standard'))) {
          workflow.name = titleState
          workflow.selectable = workflowMenuState
        }
        effectedWorkflow = workflow
        return workflow
      })
    } else {
      effectedWorkflow = getNewWorkflow()
      allWorkflows = [...allWorkflows, effectedWorkflow]
    }
    if(validateWorkflows(allWorkflows)) {
      const callback = () => {
        if(isEdit) {
          // if its an edit we are already on the workflow so we shouldn't need to do anything
        } else {
          // if its a new workflow, navigate to it and automatically open the add step modal
          
          // not sure if we might be in a workflow or screen path so we will split on both
          // to be sure and the final result will only be the portion of the path that was before
          // either one of them. We can then apply the new path suffix to that.
          const workflowSplit = pathname.split('/workflow')
          const screenSplit = workflowSplit[0].split('/screen')
          push(`${screenSplit[0]}/workflow/${effectedWorkflow.path}`)
          openConfigureStepModal(effectedWorkflow, false)
        }
      }
      saveWorkflowsToApi(allWorkflows, callback)
    }
  }, [contextWorkflowConfigs, currentScreenType, titleState, urlState, workflowMenuState, saveWorkflowsToApi, pathname, isEdit])

  const confirmDeleteModal = useCallback((titleState) => {
    dispatch(openModal('confirm_modal', {
      title: 'Delete Screen',
      content: (
        <DeleteWorkflowModal closeParent={closeSelf} workflows={contextWorkflowConfigs} workflowId={contextWorkflowConfigsId} currentWorkflowPath={currentWorkflowPath} workflowName={titleState}/>
      ),
      closeable: true
    }))
    return true // leave modal open
  }, [contextWorkflowConfigs, contextWorkflowConfigsId, currentWorkflowPath, closeSelf])

  return (
    <StandardModalContentNew
      cancelHandler={closeSelf}
      okHandler={isEdit ? () => confirmDeleteModal(titleState) : null}
      thirdHandler={updateWorkflow}
      okText={'Delete'}
      okClassName={'btn-warning'}
      thirdText={'Save'}
      cancelText={'Cancel'}
      closeSelf={closeSelf}
    >
      <div className='edit-screen-modal'>
        <div className='title-input'>
          <InputGroupText
            type="text"
            title="Title"
            placeholder=""
            aria-label={'screen title'}
            value={titleState}
            style={{ textAlign: 'left' }}
            onChange={e => setTitleState(e.target.value)}
          />
        </div>
        <div className='url-input'>
          <InputGroupText
            type="text"
            placeholder=""
            title="URL Name"
            aria-label={'url name'}
            value={urlState}
            style={{ textAlign: 'left' }}
            disabled={isEdit}
            onChange={e => setUrlState(e.target.value)}
          />
        </div>
        <div className='screen-menu-checkbox'>
          <InputGroupText
            type="checkbox"
            placeholder=""
            aria-label={'screen menu'}
            title={"Include in workflow menu?"}
            checked={workflowMenuState}
            style={{ textAlign: 'left' }}
            onChange={e => setWorkflowMenuState(e.target.checked)}
          />
        </div>
      </div>
    </StandardModalContentNew>
  )
}

EditScreenModal.propTypes = {
  onUpdate: PropTypes.func,
  closeSelf: PropTypes.func.isRequired,
}

const enhance = compose(
  withWindowScreenWorkflowConfigs()
)

export default enhance(EditScreenModal)
